import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { PublicPlayer } from '@majority-report/shared';
import { Hearts } from './Hearts';
import { Pill } from './ui';
import { pop } from '../styles/GlobalStyle';

const Chip = styled.div<{ $you: boolean; $out: boolean; $ready: boolean }>`
  animation: ${pop} 340ms ${({ theme }) => theme.transitions.bounce} both;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 15px;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 2.5px solid transparent;
  box-shadow: ${({ theme }) => theme.shadows.puff};
  transition: all 240ms ${({ theme }) => theme.transitions.bounce};

  ${({ $you, theme }) =>
    $you &&
    css`
      border-color: ${theme.colors.bubblegum};
      background: ${theme.colors.surfaceSoft};
    `}

  /* A green rim is the at-a-glance "locked in" signal during the question phase. */
  ${({ $ready, theme }) =>
    $ready &&
    css`
      border-color: ${theme.colors.mintDeep};
      box-shadow: 0 0 0 4px rgba(168, 230, 207, 0.35), ${theme.shadows.puff};
    `}

  ${({ $out }) =>
    $out &&
    css`
      opacity: 0.45;
      filter: grayscale(0.65);
    `}
`;

const Face = styled.div<{ $offline: boolean }>`
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  font-size: 26px;
  border-radius: 50%;
  background: linear-gradient(150deg, #fff1f8, #eef4ff);
  box-shadow: inset 0 -3px 6px rgba(180, 150, 200, 0.16);
  ${({ $offline }) =>
    $offline &&
    css`
      opacity: 0.45;
    `}
`;

const Info = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Name = styled.div`
  font-weight: 800;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tags = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Tick = styled.span`
  margin-left: auto;
  font-size: 20px;
  animation: ${pop} 300ms ${({ theme }) => theme.transitions.bounce} both;
`;

interface PlayerChipProps {
  player: PublicPlayer;
  isYou: boolean;
  isHost: boolean;
  /** The room's starting hearts, so spent ones leave an empty socket. */
  maxHearts?: number;
  /** Show the "locked in" state — only meaningful during the question phase. */
  showReady?: boolean;
  /** Play the shatter animation (reveal phase, for players who just lost a heart). */
  breaking?: boolean;
}

export function PlayerChip({
  player,
  isYou,
  isHost,
  maxHearts,
  showReady = false,
  breaking = false,
}: PlayerChipProps) {
  const { t } = useTranslation();
  const ready = showReady && player.hasAnswered && player.alive;

  return (
    <Chip $you={isYou} $out={!player.alive} $ready={ready}>
      <Face $offline={!player.connected}>{player.avatar}</Face>
      <Info>
        <Name>{player.nickname}</Name>
        <Tags>
          {player.alive ? (
            <Hearts hearts={player.hearts} max={maxHearts} size={15} breaking={breaking} />
          ) : (
            <Pill $tone="lavender">{t('hearts.out')}</Pill>
          )}
          {player.justJoined && <Pill $tone="lemon">🆕 {t('common.justJoined')}</Pill>}
          {isHost && <Pill $tone="lemon">👑 {t('common.host')}</Pill>}
          {isYou && <Pill $tone="bubblegum">{t('common.you')}</Pill>}
          {!player.connected && <Pill $tone="lavender">💤</Pill>}
        </Tags>
      </Info>
      {ready && <Tick aria-hidden="true">✅</Tick>}
    </Chip>
  );
}
